import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import createContext from '../support/helpers/createContext';
import { fetchJson } from '../support/useFetch';

type OpenState = 'CLOSED' | 'MANUAL_OPENED' | 'AUTO_OPENED';

type ReleaseNotesContext = {
  latestRelease: Release | null;
  openState: OpenState;
  showReleaseNotes: () => void;
  hideReleaseNotes: () => void;
};

const [useReleaseNotes, Provider] = createContext<ReleaseNotesContext>();

export { useReleaseNotes };

type Props = {
  children: React.ReactNode;
};

type Release = {
  name: string;
  notes: string;
  details: string;
};

type ApiResult = { latestRelease: Release };

export function ReleaseNotesProvider(props: Props) {
  const [openState, setOpenState] = useState<OpenState>('CLOSED');
  const [latestRelease, setLatestRelease] = useState<Release | null>(null);

  useQuery(
    ['release-notes'],
    () => fetchJson<ApiResult>('/api/releases/latest'),
    {
      onError: (error) => {
        // eslint-disable-next-line no-console
        console.error(error);
        setLatestRelease({ name: '', notes: '', details: '' });
      },
      onSuccess: (data) => {
        const { latestRelease } = data;
        setLatestRelease(latestRelease);
        const lastDismissed = localStorage.getItem(
          'last-dismissed-release-notes',
        );
        if (
          latestRelease.notes !== '' &&
          latestRelease.name !== lastDismissed
        ) {
          setOpenState('AUTO_OPENED');
        }
      },
    },
  );

  const contextValue = useMemo(
    () => ({
      openState,
      latestRelease,
      showReleaseNotes: () => setOpenState('MANUAL_OPENED'),
      hideReleaseNotes: () => setOpenState('CLOSED'),
    }),
    [openState, latestRelease],
  );

  return <Provider value={contextValue}>{props.children}</Provider>;
}
